import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  AddCircle, Home, Building, Eye, EyeSlash, Edit2, Trash, Chart2, SearchNormal1,
  ArrowDown2, ArrowLeft2, ArrowRight2, Call, Message, Calendar, Location, GalleryAdd,
} from "iconsax-reactjs";
import { db, storage } from "../firebase";
import {
  collection, onSnapshot, doc, setDoc, query, where
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { consolidateLeads } from "../utils/leadUtils";
import { queueLeadChat } from "../utils/chatShared";
import { buildListingDoc, softDeleteListing, setListingHidden } from "../utils/listingPayload";
import { getListingCardInfo, getListingTypeOf, getListingStatus } from "../utils/listingDisplay";
import { suggestTitle } from "../shared/listingRules";
import {
  LISTING_STEPS, createEmptyValues, valuesFromListing, getStepErrors, getFirstInvalidStep,
  LOCATION_STEP, hasPinnedCoordinates,
} from "./listing/listingFormModel";
import {
  PropertyStep, LocationFields, DetailsStep, PricingStep, PublishFields, ListingSummary,
} from "./listing/ListingFormFields";
import Select from "./Select";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./DealerListings.css";

// Fix default marker icon issue in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const PRESET_IMAGES = [
  "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=800&q=80"
];

const ITEMS_PER_PAGE = 8;
const DEFAULT_LAT = 12.9716;
const DEFAULT_LNG = 77.5946;
const LAST_STEP = LISTING_STEPS.length - 1;
const CATEGORY_PILLS = [
  { id: "all", label: "All" },
  { id: "buy", label: "Buy" },
  { id: "rent", label: "Rent" },
  { id: "lease", label: "Lease" },
];

// Demand bars: one bar per day for the last 14 days, oldest first.
const DEMAND_DAYS = 14;
const DEMAND_MIN_PX = 2;
const DEMAND_MAX_PX = 22;
const EMPTY_DEMAND = new Array(DEMAND_DAYS).fill(0);
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Live leads rail
const RAIL_LIMIT = 8;
const SKELETON_ROWS = 5;
const SKELETON_LEADS = 4;

const STATUS_CHIP = {
  active: "chip-success",
  inactive: "",
  pending: "chip-warning",
  deleted: "chip-danger",
};

const LEAD_ACTIONS = {
  chat: { label: "Chat", Icon: Message },
  call: { label: "Call", Icon: Call },
  visit: { label: "Visit", Icon: Calendar },
  enquiry: { label: "Enquiry", Icon: Calendar },
};

/** Milliseconds for a Firestore Timestamp, or anything Date can read. */
function toMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function startOfDay(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** When a lead came in, for the demand bars: createdAt, else updatedAt. */
const leadArrivedAt = (lead) => toMillis(lead.createdAt) ?? toMillis(lead.updatedAt);
/** Latest activity on a lead, the same order consolidateLeads sorts by. */
const leadActiveAt = (lead) => toMillis(lead.updatedAt) ?? toMillis(lead.createdAt);
const isNewLead = (lead) => (lead.status || "New") === "New";

function formatAgo(ms, now) {
  const minutes = Math.floor(Math.max(0, now - ms) / MINUTE_MS);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days <= 30) return days === 1 ? "1 day ago" : `${days} days ago`;
  return new Date(ms).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

/** How urgent a New lead is by age; leads already handled stay neutral. */
function leadAgeClass(lead, ms, now) {
  if (!isNewLead(lead) || ms === null) return "";
  const age = now - ms;
  if (age < 2 * HOUR_MS) return "is-new is-fresh";
  if (age < 24 * HOUR_MS) return "is-new is-ageing";
  return "is-new is-overdue";
}

/** Page numbers around the current page, with gaps marked. */
function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [...new Set([1, current - 1, current, current + 1, total])]
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const out = [];
  pages.forEach((n, i) => {
    if (i > 0 && n - pages[i - 1] > 1) out.push(`gap-${n}`);
    out.push(n);
  });
  return out;
}

// The listing's office picker: the dealer's own number first, then each office.
const officeOptions = (offices) => [
  { value: "", label: "My personal number", hint: "Default" },
  ...offices.map((off) => ({
    value: off.id,
    label: off.officeName || "Office",
    detail: off.locality,
    hint: off.isPrimary ? "Primary" : undefined,
  })),
];

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function CheckIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

/** 14 bars, oldest to newest, scaled against the busiest day on screen. */
function DemandBars({ days, scale, allTime }) {
  const series = days || EMPTY_DEMAND;
  const total = series.reduce((sum, n) => sum + n, 0);
  const label = `${total} ${total === 1 ? "enquiry" : "enquiries"} in the last 14 days`;
  return (
    <span className="dl-demand" title={`${label}, ${allTime} all time`}>
      <span className="demand" role="img" aria-label={label}>
        {series.map((count, i) => {
          const classes = [count === 0 ? "is-empty" : "", i === DEMAND_DAYS - 1 ? "is-today" : ""].filter(Boolean).join(" ");
          const height = count === 0
            ? DEMAND_MIN_PX
            : Math.min(DEMAND_MAX_PX, Math.max(DEMAND_MIN_PX, Math.round((count / scale) * DEMAND_MAX_PX)));
          return <i key={i} className={classes || undefined} style={{ height }} />;
        })}
      </span>
      <span className="demand-total" aria-hidden="true">{total}</span>
    </span>
  );
}

/** Newest leads beside the inventory; hovering one lights up its listing row. */
function LiveLeadsRail({ leads, loaded, now, hoveredPropertyId, onHover, onReply, onViewAll, open, onToggle }) {
  const recent = useMemo(
    () => leads
      .map((lead) => ({ lead, at: leadActiveAt(lead) }))
      .sort((a, b) => (b.at || 0) - (a.at || 0))
      .slice(0, RAIL_LIMIT),
    [leads]
  );
  const newCount = leads.filter(isNewLead).length;

  return (
    <aside className={`card dl-rail${open ? "" : " is-collapsed"}`} aria-labelledby="dl-rail-title">
      <div className="dl-rail-head">
        <h2 id="dl-rail-title" className="dl-rail-title">Live leads</h2>
        {newCount > 0 && (
          <span className="count-badge" title={`${newCount} new`}>
            {newCount}<span className="sr-only"> new</span>
          </span>
        )}
        <span className="dl-rail-spacer" />
        <button type="button" className="btn-link dl-rail-all" onClick={onViewAll}>View all</button>
        <button
          type="button"
          className="btn-icon dl-rail-toggle"
          aria-expanded={open}
          aria-controls="dl-rail-body"
          aria-label={open ? "Collapse live leads" : "Expand live leads"}
          onClick={onToggle}
        >
          <ArrowDown2 size={18} aria-hidden="true" />
        </button>
      </div>

      <div id="dl-rail-body" className="dl-rail-body">
        {!loaded ? (
          <ul className="dl-leads" aria-hidden="true">
            {Array.from({ length: SKELETON_LEADS }, (_, i) => (
              <li key={i} className="dl-lead dl-lead-skeleton">
                <span className="skeleton dl-sk-line dl-sk-name" />
                <span className="skeleton dl-sk-line dl-sk-meta" />
                <span className="skeleton dl-sk-btns" />
              </li>
            ))}
          </ul>
        ) : recent.length === 0 ? (
          <p className="dl-rail-empty">No leads yet. Enquiries on your listings appear here as they arrive.</p>
        ) : (
          <ul className="dl-leads">
            {recent.map(({ lead, at }) => {
              const name = lead.tenantName || "Client";
              const actions = (Array.isArray(lead.actions) && lead.actions.length > 0 ? lead.actions : [lead.type || "enquiry"])
                .filter(Boolean);
              const linked = !!lead.propertyId && lead.propertyId === hoveredPropertyId;
              const ageClass = leadAgeClass(lead, at, now);
              return (
                <li
                  key={lead.id}
                  className={`dl-lead${linked ? " is-linked" : ""}`}
                  onMouseEnter={() => onHover(lead.propertyId || null)}
                  onMouseLeave={() => onHover(null)}
                  onFocus={() => onHover(lead.propertyId || null)}
                  onBlur={() => onHover(null)}
                >
                  <div className="dl-lead-top">
                    <span className="dl-lead-name" title={name}>{name}</span>
                    {at !== null && (
                      <time className={`dl-lead-time ${ageClass}`} dateTime={new Date(at).toISOString()}>
                        {ageClass && <span className="sr-only">New lead, </span>}
                        {formatAgo(at, now)}
                      </time>
                    )}
                  </div>
                  <div className="dl-lead-what">
                    {actions.map((act) => {
                      const meta = LEAD_ACTIONS[act] || { label: String(act), Icon: Calendar };
                      const ActionIcon = meta.Icon;
                      return (
                        <span key={act} className="chip dl-lead-chip">
                          <ActionIcon size={12} aria-hidden="true" />
                          {meta.label}
                        </span>
                      );
                    })}
                    {lead.leadType === "nearby" && <span className="chip dl-lead-chip">Nearby</span>}
                  </div>
                  <span className="dl-lead-listing" title={lead.propertyTitle || undefined}>
                    {lead.propertyTitle || "Direct enquiry"}
                  </span>
                  <div className="dl-lead-actions">
                    <button type="button" className="btn-tonal btn-sm" onClick={() => onReply(lead)} aria-label={`Reply to ${name}`}>
                      <Message size={14} aria-hidden="true" />
                      Reply
                    </button>
                    {lead.tenantPhone && (
                      <a className="btn-ghost btn-sm" href={`tel:${lead.tenantPhone}`} aria-label={`Call ${name}`}>
                        <Call size={14} aria-hidden="true" />
                        Call
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}

export default function DealerListings({ user, setActiveTab }) {
  const [properties, setProperties] = useState([]);
  const [globalFacilities, setGlobalFacilities] = useState([]);
  const [leads, setLeads] = useState([]);
  const [views, setViews] = useState([]);
  const [dealerOffices, setDealerOffices] = useState([]);
  // First snapshot not in yet: show skeletons instead of an empty desk.
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [leadsLoaded, setLeadsLoaded] = useState(false);

  // Filter/Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // The listing under the pointer or keyboard focus, in the table or the
  // leads rail, so a row and its enquiries light up together.
  const [hoveredPropertyId, setHoveredPropertyId] = useState(null);
  // Below 1200px the rail sits above the table and can be folded away.
  const [railOpen, setRailOpen] = useState(true);
  // Ticks every minute so "12 min ago" and the demand day stay current.
  const [now, setNow] = useState(() => Date.now());

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Steps where "Next" was pressed with problems; only those show errors.
  const [attemptedSteps, setAttemptedSteps] = useState({});

  // Analytics sub-modal state
  const [analyticsProperty, setAnalyticsProperty] = useState(null);

  // The listing itself, in the shape the shared listing rules expect.
  const [values, setValues] = useState(createEmptyValues);
  const initialValuesRef = useRef(JSON.stringify(createEmptyValues()));

  // Location picker states
  const [locationMethod, setLocationMethod] = useState("map");
  const [mapSearchQuery, setMapSearchQuery] = useState("");
  const [mapLat, setMapLat] = useState(DEFAULT_LAT);
  const [mapLng, setMapLng] = useState(DEFAULT_LNG);
  // Whether the dealer has actually placed the pin. The map opens on a
  // default point, and saving that untouched put listings in central Bengaluru.
  const [pinSet, setPinSet] = useState(false);

  // Media form states
  const [selectedPresetIndex, setSelectedPresetIndex] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const fileInputRef = useRef(null);

  const setField = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), MINUTE_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user?.uid) return;

    // 1. Fetch properties for this dealer
    const qProps = query(
      collection(db, "properties"),
      where("ownerId", "==", user.uid)
    );
    const unsubProps = onSnapshot(qProps, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setProperties(list);
      setPropertiesLoaded(true);
    }, (err) => {
      console.error("Listings fetch failed:", err);
      setPropertiesLoaded(true);
    });

    // 2. Fetch global facilities
    const unsubFacilities = onSnapshot(collection(db, "facilities"), (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setGlobalFacilities(list);
    });

    // 3. Fetch leads for analytics calculations
    const unsubLeads = onSnapshot(collection(db, "users", user.uid, "leads"), (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setLeads(consolidateLeads(list));
      setLeadsLoaded(true);
    }, (err) => {
      console.error("Leads fetch failed:", err);
      setLeadsLoaded(true);
    });

    // 4. Fetch property views for analytics calculations
    const unsubViews = onSnapshot(collection(db, "users", user.uid, "property_views"), (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setViews(list);
    });

    // 5. Fetch dealer offices
    const unsubOffices = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setDealerOffices(Array.isArray(d.offices) ? d.offices : []);
      }
    });

    return () => {
      unsubProps();
      unsubFacilities();
      unsubLeads();
      unsubViews();
      unsubOffices();
    };
  }, [user]);

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`);
      const data = await res.json();
      if (data && data.address) {
        const addr = data.address;
        const resolvedLocality = addr.suburb || addr.neighbourhood || addr.village || addr.city_district || "";
        const resolvedCity = addr.city || addr.town || addr.county || "";
        const street = addr.road || addr.subdivision || "";
        const houseNumber = addr.house_number || "";
        const resolvedAddress = [houseNumber, street].filter(Boolean).join(", ") || data.display_name.split(",")[0] || "";
        const postcode = String(addr.postcode || "").replace(/\s/g, "");

        setValues((prev) => ({
          ...prev,
          locality: resolvedLocality || prev.locality,
          city: resolvedCity || prev.city,
          address: resolvedAddress || prev.address,
          state: addr.state || prev.state,
          pincode: /^\d{6}$/.test(postcode) ? postcode : prev.pincode,
        }));
      }
    } catch (e) {
      console.warn("Reverse geocode failed", e);
    }
  }, []);

  // Leaflet map initialization
  useEffect(() => {
    if (!isModalOpen || wizardStep !== 1 || locationMethod !== "map") {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
      return;
    }

    const timer = setTimeout(() => {
      const container = document.getElementById("dealer-listing-map");
      if (!container || mapRef.current) return;

      const map = L.map("dealer-listing-map").setView([mapLat, mapLng], 13);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(map);

      const marker = L.marker([mapLat, mapLng], { draggable: true }).addTo(map);
      markerRef.current = marker;
      mapRef.current = map;

      marker.on("dragend", async () => {
        const pos = marker.getLatLng();
        setMapLat(pos.lat);
        setMapLng(pos.lng);
        setPinSet(true);
        await reverseGeocode(pos.lat, pos.lng);
      });

      map.on("click", async (e) => {
        marker.setLatLng(e.latlng);
        setMapLat(e.latlng.lat);
        setMapLng(e.latlng.lng);
        setPinSet(true);
        await reverseGeocode(e.latlng.lat, e.latlng.lng);
      });
    }, 300);

    return () => {
      clearTimeout(timer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
    // The map is created once per visit to the location step; later pin
    // moves update mapLat/mapLng without rebuilding it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, wizardStep, locationMethod]);

  const handleMapSearch = async (e) => {
    if (e) e.preventDefault();
    if (!mapSearchQuery.trim()) return;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearchQuery)}&limit=1`);
      const data = await res.json();
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        setMapLat(lat);
        setMapLng(lon);
        setPinSet(true);
        if (mapRef.current && markerRef.current) {
          mapRef.current.setView([lat, lon], 14);
          markerRef.current.setLatLng([lat, lon]);
        }
        await reverseGeocode(lat, lon);
      } else {
        alert("Location not found.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGoogleMapsUrlChange = async (url) => {
    setField("googleMapsUrl", url);
    if (!url.trim()) return;
    try {
      const matchAt = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/)
        || url.match(/[?&](?:q|ll|query)=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (matchAt) {
        const lat = parseFloat(matchAt[1]);
        const lng = parseFloat(matchAt[2]);
        setMapLat(lat);
        setMapLng(lng);
        setPinSet(true);
        await reverseGeocode(lat, lng);
      }
    } catch (e) {
      console.warn(e);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 9);
    setSelectedFiles(files);
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  const uploadImages = async (pid) => {
    if (selectedFiles.length === 0) return [PRESET_IMAGES[selectedPresetIndex]];
    const urls = [];
    setUploadProgress(1);
    for (let i = 0; i < selectedFiles.length; i++) {
      const sRef = ref(storage, `properties/${pid}/image_${i}_${Date.now()}`);
      const task = uploadBytesResumable(sRef, selectedFiles[i]);
      await new Promise((res, rej) => {
        task.on("state_changed",
          (s) => setUploadProgress(Math.round(((i / selectedFiles.length) + (s.bytesTransferred / s.totalBytes / selectedFiles.length)) * 100)),
          rej,
          async () => {
            urls.push(await getDownloadURL(task.snapshot.ref));
            res();
          }
        );
      });
    }
    setUploadProgress(100);
    return urls;
  };

  const resolveStateName = async () => {
    if (values.state) return values.state;
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(`${values.address}, ${values.locality}, ${values.city}`)}&limit=1`);
      const d = await r.json();
      if (d?.length > 0) {
        const pts = (d[0].display_name || "").split(",");
        if (pts.length > 2) return pts[pts.length - 3].trim();
      }
    } catch (e) {}
    // Same fallback the app uses when the state can't be resolved.
    return "Karnataka";
  };

  const resetForm = () => {
    const empty = createEmptyValues();
    setValues(empty);
    initialValuesRef.current = JSON.stringify(empty);
    setSelectedFiles([]);
    setPreviewUrls([]);
    setSelectedPresetIndex(0);
    setMapLat(DEFAULT_LAT);
    setMapLng(DEFAULT_LNG);
    setPinSet(false);
    setMapSearchQuery("");
    setLocationMethod("map");
    setWizardStep(0);
    setAttemptedSteps({});
    setUploadProgress(0);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
    setEditingProperty(null);
  };

  // Clicking outside or the close button shouldn't silently throw away a
  // half-filled listing.
  const requestClose = () => {
    if (isSaving) return;
    const dirty = JSON.stringify(values) !== initialValuesRef.current || selectedFiles.length > 0;
    if (dirty && !window.confirm("Discard this listing? Your changes will be lost.")) return;
    closeModal();
  };

  const openCreate = () => {
    setEditingProperty(null);
    resetForm();
    setIsModalOpen(true);
  };

  const handleEditPress = (p) => {
    const next = valuesFromListing(p);
    // An office deleted before listings were reassigned leaves a dangling
    // id, so fall back to the default instead of saving it back.
    next.officeId = p.officeId && dealerOffices.some((o) => o.id === p.officeId) ? p.officeId : "";
    setEditingProperty(p);
    setValues(next);
    initialValuesRef.current = JSON.stringify(next);
    setSelectedFiles([]);
    setPreviewUrls([]);
    setSelectedPresetIndex(0);
    // A listing saved without a real pin opens with none, so the dealer has
    // to place it before saving, rather than keeping the default point.
    const pinned = hasPinnedCoordinates(p.latitude, p.longitude);
    setMapLat(pinned ? Number(p.latitude) : DEFAULT_LAT);
    setMapLng(pinned ? Number(p.longitude) : DEFAULT_LNG);
    setPinSet(pinned);
    setMapSearchQuery("");
    setLocationMethod(p.googleMapsUrl ? "url" : "map");
    setWizardStep(0);
    setAttemptedSteps({});
    setIsModalOpen(true);
  };

  const goToStep = (step) => {
    // Arriving at the last step with no title: start from a suggestion.
    if (step === LAST_STEP && !String(values.title || "").trim()) {
      setField("title", suggestTitle(values));
    }
    setWizardStep(step);
  };

  const handleNext = () => {
    const errors = getStepErrors(wizardStep, values);
    const pinMissing = wizardStep === LOCATION_STEP && !pinSet;
    if (Object.keys(errors).length > 0 || pinMissing) {
      setAttemptedSteps((prev) => ({ ...prev, [wizardStep]: true }));
      return;
    }
    goToStep(wizardStep + 1);
  };

  const handleSave = async () => {
    const invalidStep = getFirstInvalidStep(values);
    if (invalidStep !== -1) {
      setAttemptedSteps((prev) => ({ ...prev, [invalidStep]: true }));
      setWizardStep(invalidStep);
      return;
    }
    if (!pinSet) {
      setAttemptedSteps((prev) => ({ ...prev, [LOCATION_STEP]: true }));
      setWizardStep(LOCATION_STEP);
      return;
    }
    setIsSaving(true);
    try {
      const isEdit = !!editingProperty;
      const dRef = isEdit ? doc(db, "properties", editingProperty.id) : doc(collection(db, "properties"));
      const images = isEdit && selectedFiles.length === 0
        ? (editingProperty.images || [])
        : await uploadImages(dRef.id);
      const stateName = await resolveStateName();

      const data = buildListingDoc({
        id: dRef.id,
        values,
        latitude: mapLat,
        longitude: mapLng,
        stateName,
        images,
        owner: {
          ownerId: user.uid,
          ownerName: user.name || "Dealer",
          ownerPhone: user.contactNumber || user.phoneNumber || "",
          ownerAvatar: user.avatarUrl || user.avatar || "",
          ownerRole: "dealer",
        },
        isEdit,
      });

      await setDoc(dRef, data, { merge: true });
      closeModal();
    } catch (err) {
      console.error("Save listing failed:", err);
      alert(`Couldn't save this listing: ${err?.message || err}`);
    } finally {
      setIsSaving(false);
      setUploadProgress(0);
    }
  };

  // Tombstone (same as deleting in the app): links, saved lists and leads
  // keep resolving to "no longer available".
  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title || "this listing"}"? It will be removed from search and its Reels hidden.`)) return;
    try {
      await softDeleteListing(p.id, "Removed from dealer portal");
    } catch (err) {
      console.error("Delete failed:", err);
      alert(`Couldn't delete this listing: ${err?.message || err}`);
    }
  };

  const handleToggleHidden = async (p) => {
    const hide = getListingStatus(p) !== "inactive";
    try {
      await setListingHidden(p.id, hide);
    } catch (err) {
      console.error("Status change failed:", err);
      alert(`Couldn't ${hide ? "hide" : "show"} this listing: ${err?.message || err}`);
    }
  };

  const goToTab = useCallback((tab) => {
    if (typeof setActiveTab === "function") setActiveTab(tab);
  }, [setActiveTab]);

  // Same hand-off as the Leads page: the inbox opens on this tenant's chat.
  const replyToLead = (lead) => {
    queueLeadChat(lead);
    goToTab("dealer-chat");
  };

  const liveProperties = properties.filter((p) => getListingStatus(p) !== "deleted");
  const filteredProperties = liveProperties.filter((p) => {
    const q = searchQuery.trim().toLowerCase();
    const s = !q || [p.title, p.locality, p.city, p.societyName].some((x) => String(x || "").toLowerCase().includes(q));
    const c = activeCategory === "all" || getListingTypeOf(p) === activeCategory;
    return s && c;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeCategory]);

  const totalPages = Math.ceil(filteredProperties.length / ITEMS_PER_PAGE);
  // A delete can empty the last page; show the page before it instead.
  const page = Math.min(currentPage, Math.max(1, totalPages));
  const startIdx = (page - 1) * ITEMS_PER_PAGE;
  const paginatedProperties = filteredProperties.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  const stepErrors = attemptedSteps[wizardStep] ? getStepErrors(wizardStep, values) : {};
  const previewImage = previewUrls[0] || (editingProperty && selectedFiles.length === 0 && editingProperty.images?.[0]) || PRESET_IMAGES[selectedPresetIndex];

  // Per-listing counts for the table, built once per snapshot.
  const viewCounts = useMemo(() => {
    const counts = new Map();
    views.forEach((v) => counts.set(v.propertyId, (counts.get(v.propertyId) || 0) + 1));
    return counts;
  }, [views]);

  const leadCounts = useMemo(() => {
    const counts = new Map();
    leads.forEach((l) => counts.set(l.propertyId, (counts.get(l.propertyId) || 0) + 1));
    return counts;
  }, [leads]);

  // Enquiries per listing per day over the last 14 days, oldest day first.
  const today = startOfDay(now);
  const demandByProperty = useMemo(() => {
    const byProperty = new Map();
    leads.forEach((lead) => {
      if (!lead.propertyId) return;
      const at = leadArrivedAt(lead);
      if (at === null) return;
      const daysAgo = Math.max(0, Math.round((today - startOfDay(at)) / DAY_MS));
      if (daysAgo >= DEMAND_DAYS) return;
      let days = byProperty.get(lead.propertyId);
      if (!days) {
        days = new Array(DEMAND_DAYS).fill(0);
        byProperty.set(lead.propertyId, days);
      }
      days[DEMAND_DAYS - 1 - daysAgo] += 1;
    });
    return byProperty;
  }, [leads, today]);

  const demandScale = Math.max(
    1,
    ...paginatedProperties.map((p) => Math.max(0, ...(demandByProperty.get(p.id) || EMPTY_DEMAND)))
  );

  const statusCounts = liveProperties.reduce((acc, p) => {
    const status = getListingStatus(p);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const countParts = [`${statusCounts.active || 0} live`];
  if (statusCounts.inactive) countParts.push(`${statusCounts.inactive} hidden`);
  if (statusCounts.pending) countParts.push(`${statusCounts.pending} pending review`);

  // Property Analytics calculations
  const getPropertyAnalytics = (pid) => {
    const propViews = views.filter(v => v.propertyId === pid);
    const propLeads = leads.filter(l => l.propertyId === pid);
    const conversionRate = propViews.length > 0
      ? ((propLeads.length / propViews.length) * 100).toFixed(1) + "%"
      : "0.0%";

    // Count every channel each tenant used, not just their latest action.
    const usedChannel = (l, ...channels) =>
      (l.actions?.length ? l.actions : [l.type]).some((a) => channels.includes(a));
    const callCount = propLeads.filter(l => usedChannel(l, "call")).length;
    const chatCount = propLeads.filter(l => usedChannel(l, "chat")).length;
    const visitCount = propLeads.filter(l => usedChannel(l, "visit", "enquiry")).length;

    // Device breakdown
    const mobileViews = propViews.filter(v => v.deviceType === "mobile" || v.deviceType === "android" || v.deviceType === "ios").length;
    const desktopViews = propViews.filter(v => v.deviceType === "desktop" || v.deviceType === "web").length;

    return {
      views: propViews.length,
      leads: propLeads.length,
      conversionRate,
      channels: { call: callCount, chat: chatCount, visit: visitCount },
      devices: { mobile: mobileViews, desktop: desktopViews }
    };
  };

  const analyticsInfo = analyticsProperty ? getListingCardInfo(analyticsProperty) : null;
  const analytics = analyticsProperty ? getPropertyAnalytics(analyticsProperty.id) : null;
  const channelShare = (n) => (analytics && analytics.leads ? (n / analytics.leads) * 100 : 0);

  const tableHead = (
    <thead>
      <tr>
        <th scope="col">Listing</th>
        <th scope="col">Price</th>
        <th scope="col">Status</th>
        <th scope="col" className="num">Views</th>
        <th scope="col">Enquiries <span className="dl-th-note">14 days</span></th>
        <th scope="col" className="dl-th-actions"><span className="sr-only">Actions</span></th>
      </tr>
    </thead>
  );

  const renderRow = (p) => {
    const info = getListingCardInfo(p);
    const isHidden = info.status === "inactive";
    const name = p.title || "this listing";
    const cover = p.images?.[0];
    const place = [p.locality, p.city].filter(Boolean).join(", ");
    const specs = [
      info.typeLabel,
      info.configLabel && info.configLabel !== info.typeLabel ? info.configLabel : "",
      info.bathroomsLabel,
      info.areaLabel,
    ].filter(Boolean);
    const office = p.officeId ? dealerOffices.find((o) => o.id === p.officeId) : null;
    const linked = hoveredPropertyId === p.id;

    return (
      <tr
        key={p.id}
        className={`dl-row${isHidden ? " is-hidden" : ""}${linked ? " is-linked" : ""}`}
        onMouseEnter={() => setHoveredPropertyId(p.id)}
        onMouseLeave={() => setHoveredPropertyId(null)}
        onFocus={() => setHoveredPropertyId(p.id)}
        onBlur={() => setHoveredPropertyId(null)}
      >
        <td className="dl-td-listing">
          <div className="dl-listing">
            <span className="dl-thumb">
              <Home size={20} aria-hidden="true" />
              {cover && (
                <img src={cover} alt="" loading="lazy" onError={(e) => { e.currentTarget.hidden = true; }} />
              )}
            </span>
            <div className="dl-listing-text">
              <span className="dl-listing-title" title={p.title || undefined}>{p.title || "Untitled listing"}</span>
              <span className="dl-listing-place">{place || "Location not set"}</span>
              {(specs.length > 0 || office) && (
                <span className="dl-listing-specs">
                  {specs.join(" · ")}
                  {office && (
                    <span className="dl-office" title="Listing office">
                      {specs.length > 0 && " · "}
                      <Building size={12} variant="Linear" aria-hidden="true" />
                      {office.officeName || "Assigned office"}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </td>
        <td className="dl-td-price">
          <span className="dl-price">
            {info.priceLabel}
            {info.priceUnit && <span className="dl-price-unit"> {info.priceUnit}</span>}
          </span>
          <span className="dl-price-type">{info.badge}</span>
        </td>
        <td className="dl-td-status">
          <span className={`chip chip-dot ${STATUS_CHIP[info.status] || ""}`}>{info.statusLabel || "Live"}</span>
        </td>
        <td className="num dl-td-views" data-label="Views">{viewCounts.get(p.id) || 0}</td>
        <td className="dl-td-demand" data-label="Enquiries, 14 days">
          {leadsLoaded ? (
            <DemandBars days={demandByProperty.get(p.id)} scale={demandScale} allTime={leadCounts.get(p.id) || 0} />
          ) : (
            <span className="skeleton dl-sk-demand" />
          )}
        </td>
        <td className="dl-td-actions">
          <div className="dl-actions">
            <button type="button" className="btn-icon" onClick={() => handleEditPress(p)} aria-label={`Edit ${name}`} title="Edit listing">
              <Edit2 size={18} aria-hidden="true" />
            </button>
            <button type="button" className="btn-icon" onClick={() => setAnalyticsProperty(p)} aria-label={`Analytics for ${name}`} title="Analytics">
              <Chart2 size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="btn-icon"
              onClick={() => handleToggleHidden(p)}
              aria-label={isHidden ? `Show ${name} in search` : `Hide ${name} from search`}
              title={isHidden ? "Show in search" : "Hide from search"}
            >
              {isHidden ? <Eye size={18} aria-hidden="true" /> : <EyeSlash size={18} aria-hidden="true" />}
            </button>
            <button type="button" className="btn-icon dl-btn-delete" onClick={() => handleDelete(p)} aria-label={`Delete ${name}`} title="Delete listing">
              <Trash size={18} aria-hidden="true" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const skeletonRows = Array.from({ length: SKELETON_ROWS }, (_, i) => (
    <tr key={`sk-${i}`} className="dl-row dl-row-skeleton" aria-hidden="true">
      <td className="dl-td-listing">
        <div className="dl-listing">
          <span className="skeleton dl-sk-thumb" />
          <div className="dl-listing-text">
            <span className="skeleton dl-sk-line dl-sk-title" />
            <span className="skeleton dl-sk-line dl-sk-meta" />
          </div>
        </div>
      </td>
      <td className="dl-td-price"><span className="skeleton dl-sk-line dl-sk-price" /></td>
      <td className="dl-td-status"><span className="skeleton dl-sk-chip" /></td>
      <td className="num dl-td-views"><span className="skeleton dl-sk-line dl-sk-num" /></td>
      <td className="dl-td-demand"><span className="skeleton dl-sk-demand" /></td>
      <td className="dl-td-actions"><span className="skeleton dl-sk-actions" /></td>
    </tr>
  ));

  return (
    <div className="dl-desk">
      <div className="toolbar dl-toolbar">
        <div className="segmented dl-filter" role="group" aria-label="Filter by listing type">
          {CATEGORY_PILLS.map((pill) => (
            <button
              key={pill.id}
              type="button"
              className={activeCategory === pill.id ? "active" : ""}
              aria-pressed={activeCategory === pill.id}
              onClick={() => setActiveCategory(pill.id)}
            >
              {pill.label}
            </button>
          ))}
        </div>
        <div className="search-field dl-search">
          <SearchNormal1 size={16} aria-hidden="true" />
          <input
            type="search"
            className="input"
            placeholder="Search title, locality or project"
            aria-label="Search listings"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="toolbar-spacer" />
        <button type="button" className="btn-primary dl-add" onClick={openCreate}>
          <AddCircle size={18} aria-hidden="true" />
          Add listing
        </button>
      </div>

      <p className="dl-count">
        {propertiesLoaded ? countParts.join(", ") : <span className="skeleton dl-sk-count" aria-hidden="true" />}
      </p>

      <LiveLeadsRail
        leads={leads}
        loaded={leadsLoaded}
        now={now}
        hoveredPropertyId={hoveredPropertyId}
        onHover={setHoveredPropertyId}
        onReply={replyToLead}
        onViewAll={() => goToTab("dealer-leads")}
        open={railOpen}
        onToggle={() => setRailOpen((o) => !o)}
      />

      {!propertiesLoaded ? (
        <div className="table-wrap dl-table-area" aria-busy="true">
          <table className="table dl-table">
            {tableHead}
            <tbody>{skeletonRows}</tbody>
          </table>
        </div>
      ) : filteredProperties.length === 0 ? (
        <div className="table-wrap dl-table-area">
          <div className="empty-state">
            <span className="empty-state-icon"><Home size={24} aria-hidden="true" /></span>
            {liveProperties.length === 0 ? (
              <>
                <h3>No listings yet</h3>
                <p>Add your first listing to start receiving buyer leads. Each listing shows its views and the last 14 days of enquiries here.</p>
              </>
            ) : (
              <>
                <h3>No listings match</h3>
                <p>Nothing matches this search and filter. Try another search or show every listing.</p>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}
                >
                  Clear filters
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="table-wrap dl-table-area">
          <table className="table dl-table">
            {tableHead}
            <tbody>{paginatedProperties.map(renderRow)}</tbody>
          </table>
        </div>
      )}

      {propertiesLoaded && totalPages > 1 && (
        <nav className="pagination dl-pagination" aria-label="Listing pages">
          <span>
            Showing {startIdx + 1} to {Math.min(startIdx + ITEMS_PER_PAGE, filteredProperties.length)} of {filteredProperties.length}
          </span>
          <div className="pagination-pages">
            <button type="button" className="page-btn" disabled={page === 1} onClick={() => setCurrentPage(page - 1)} aria-label="Previous page">
              <ArrowLeft2 size={16} aria-hidden="true" />
            </button>
            {pageWindow(page, totalPages).map((n) => (typeof n === "number" ? (
              <button
                key={n}
                type="button"
                className={`page-btn${n === page ? " active" : ""}`}
                aria-current={n === page ? "page" : undefined}
                aria-label={`Page ${n}`}
                onClick={() => setCurrentPage(n)}
              >
                {n}
              </button>
            ) : (
              <span key={n} className="dl-page-gap" aria-hidden="true">…</span>
            )))}
            <button type="button" className="page-btn" disabled={page === totalPages} onClick={() => setCurrentPage(page + 1)} aria-label="Next page">
              <ArrowRight2 size={16} aria-hidden="true" />
            </button>
          </div>
        </nav>
      )}

      {/* Add / Edit Listing Wizard Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={requestClose}>
          <div
            className="modal lf-wide dl-wizard"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dl-wizard-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="dl-wizard-title">{editingProperty ? "Edit property listing" : "Create property listing"}</h2>
              <button type="button" className="btn-icon" aria-label="Close" onClick={requestClose}>
                <CloseIcon />
              </button>
            </div>

            <ol className="dl-steps" aria-label="Listing steps">
              {LISTING_STEPS.map((s, i) => {
                const done = i < wizardStep;
                const current = i === wizardStep;
                return (
                  <li
                    key={s.id}
                    className={`dl-step${current ? " is-current" : ""}${done ? " is-done" : ""}`}
                    aria-current={current ? "step" : undefined}
                  >
                    <span className="dl-step-marker">{done ? <CheckIcon size={12} /> : i + 1}</span>
                    <span className="dl-step-label">
                      {s.label}
                      {done && <span className="sr-only"> (done)</span>}
                    </span>
                  </li>
                );
              })}
            </ol>

            <div className="dl-wizard-body">
              {wizardStep === 0 && (
                <PropertyStep values={values} setValues={setValues} errors={stepErrors} />
              )}

              {wizardStep === 1 && (
                <div className="dl-wz-panel">
                  <section className="dl-wz-section">
                    <h3 className="dl-wz-title">Pin the property location</h3>
                    <div className="segmented dl-method" role="group" aria-label="How to set the location">
                      <button
                        type="button"
                        className={locationMethod === "map" ? "active" : ""}
                        aria-pressed={locationMethod === "map"}
                        onClick={() => setLocationMethod("map")}
                      >
                        Search on map
                      </button>
                      <button
                        type="button"
                        className={locationMethod === "url" ? "active" : ""}
                        aria-pressed={locationMethod === "url"}
                        onClick={() => setLocationMethod("url")}
                      >
                        Paste Google Maps link
                      </button>
                    </div>
                    {locationMethod === "url" ? (
                      <div className="field">
                        <label className="field-label" htmlFor="dl-gmaps-url">Google Maps URL</label>
                        <input
                          id="dl-gmaps-url"
                          type="text"
                          className="input"
                          placeholder="https://www.google.com/maps/place/..."
                          value={values.googleMapsUrl}
                          onChange={(e) => handleGoogleMapsUrlChange(e.target.value)}
                        />
                        <span className="field-hint">Use a link that includes the map coordinates; the address below fills in from it.</span>
                      </div>
                    ) : (
                      <>
                        <div className="dl-map-search">
                          <div className="search-field">
                            <SearchNormal1 size={16} aria-hidden="true" />
                            <input
                              type="text"
                              className="input"
                              aria-label="Search locality or landmark"
                              placeholder="Search locality or landmark"
                              value={mapSearchQuery}
                              onChange={e => setMapSearchQuery(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") handleMapSearch(e); }}
                            />
                          </div>
                          <button type="button" className="btn-secondary" onClick={handleMapSearch}>Search</button>
                        </div>
                        <div id="dealer-listing-map" className="dl-map" />
                        <p className="dl-map-coords">
                          <Location size={14} aria-hidden="true" />
                          {pinSet ? `Latitude ${mapLat.toFixed(5)}, longitude ${mapLng.toFixed(5)}` : "No pin yet"}
                          <span className="dl-map-tip">Click the map or drag the pin to move it.</span>
                        </p>
                      </>
                    )}
                    {attemptedSteps[LOCATION_STEP] && !pinSet && (
                      <div className="field-error" role="alert">
                        Pin where the property is: click the map or drag the pin, search for the place, or paste a Google Maps link that includes coordinates.
                      </div>
                    )}
                  </section>

                  <section className="dl-wz-section">
                    <h3 className="dl-wz-title">Address</h3>
                    <LocationFields values={values} setField={setField} errors={stepErrors} />
                  </section>
                </div>
              )}

              {wizardStep === 2 && (
                <DetailsStep values={values} setField={setField} errors={stepErrors} />
              )}

              {wizardStep === 3 && (
                <PricingStep values={values} setField={setField} setValues={setValues} errors={stepErrors} />
              )}

              {wizardStep === LAST_STEP && (
                <div className="dl-wz-panel">
                  <section className="dl-wz-section">
                    <ListingSummary values={values} imageUrl={previewImage} />
                  </section>

                  <section className="dl-wz-section">
                    <PublishFields values={values} setField={setField} errors={stepErrors} facilities={globalFacilities} />
                  </section>

                  {dealerOffices.length > 0 && (
                    <section className="dl-wz-section">
                      <div className="field">
                        <label className="field-label" htmlFor="dl-office">Listing office</label>
                        <Select
                          id="dl-office"
                          value={values.officeId || ""}
                          onChange={(id) => setField("officeId", id)}
                          placeholder="Choose an office"
                          options={officeOptions(dealerOffices)}
                        />
                        <span className="field-hint">
                          Buyers who call or chat about this listing reach the selected office’s number. With the default they reach the phone number on your profile.
                        </span>
                      </div>
                    </section>
                  )}

                  <section className="dl-wz-section">
                    <h3 className="dl-wz-title">Property photos</h3>
                    <div className="dl-photos">
                      <input
                        id="dl-photos"
                        type="file"
                        className="dl-upload-input"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept="image/*"
                      />
                      <label htmlFor="dl-photos" className="dl-upload">
                        <span className="dl-upload-icon"><GalleryAdd size={22} aria-hidden="true" /></span>
                        <span className="dl-upload-text">
                          <span className="dl-upload-title">
                            {selectedFiles.length > 0
                              ? `${selectedFiles.length} ${selectedFiles.length === 1 ? "photo" : "photos"} selected`
                              : "Upload photos (up to 9)"}
                          </span>
                          <span className="field-hint">
                            {selectedFiles.length > 0
                              ? "Choose again to replace them. The first photo is the cover."
                              : "Choose up to 9 images. The first photo is the cover."}
                          </span>
                        </span>
                      </label>
                      {previewUrls.length > 0 && (
                        <ul className="dl-thumbs" aria-label="Selected photos">
                          {previewUrls.map((url, index) => (
                            <li key={index} className="dl-thumbs-item">
                              <img src={url} alt={`Preview ${index + 1}`} />
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {selectedFiles.length === 0 && !editingProperty && (
                      <div className="field dl-presets-field">
                        <span className="field-label" id="dl-presets-label">Or use a preset image for now</span>
                        <div className="dl-presets" role="group" aria-labelledby="dl-presets-label">
                          {PRESET_IMAGES.map((img, i) => {
                            const selected = selectedPresetIndex === i;
                            return (
                              <button
                                key={i}
                                type="button"
                                className={`dl-preset${selected ? " is-selected" : ""}`}
                                aria-pressed={selected}
                                aria-label={`Preset image ${i + 1}`}
                                onClick={() => setSelectedPresetIndex(i)}
                              >
                                <img src={img} alt="" />
                                {selected && <span className="dl-preset-check"><CheckIcon size={12} /></span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {editingProperty && selectedFiles.length === 0 && (
                      <p className="field-hint dl-photos-note">
                        The listing keeps its current {editingProperty.images?.length || 0} photo(s) unless you upload new ones.
                      </p>
                    )}
                  </section>
                </div>
              )}
            </div>

            <div className="modal-footer dl-wizard-footer">
              {wizardStep > 0 && (
                <button type="button" className="btn-secondary" onClick={() => setWizardStep(wizardStep - 1)} disabled={isSaving}>Back</button>
              )}
              {uploadProgress > 0 && (
                <div className="dl-progress">
                  <div className="dl-progress-head">
                    <span>Uploading photos</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div
                    className="dl-progress-track"
                    role="progressbar"
                    aria-label="Uploading photos"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={uploadProgress}
                  >
                    <div className="dl-progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}
              {wizardStep < LAST_STEP ? (
                <button type="button" className="btn-primary ml-auto" onClick={handleNext}>Next</button>
              ) : (
                <button type="button" className="btn-primary ml-auto" disabled={isSaving} onClick={handleSave}>
                  {isSaving && <span className="btn-spinner" aria-hidden="true" />}
                  {isSaving ? "Saving listing" : editingProperty ? "Update listing" : "Publish property"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Property Analytics Detailed Modal */}
      {analyticsProperty && (
        <div className="modal-overlay" onClick={() => setAnalyticsProperty(null)}>
          <div
            className="modal dl-analytics"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dl-analytics-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="dl-analytics-title">Property analytics</h2>
              <button type="button" className="btn-icon" aria-label="Close" onClick={() => setAnalyticsProperty(null)}>
                <CloseIcon />
              </button>
            </div>

            <div className="modal-body">
              <div className="dl-an-meta">
                <span className="dl-thumb">
                  <Home size={20} aria-hidden="true" />
                  {analyticsProperty.images?.[0] && (
                    <img src={analyticsProperty.images[0]} alt="" onError={(e) => { e.currentTarget.hidden = true; }} />
                  )}
                </span>
                <div className="dl-listing-text">
                  <h3 className="dl-an-name">{analyticsProperty.title}</h3>
                  <p className="dl-an-place">
                    <Location size={14} aria-hidden="true" />
                    {[analyticsProperty.locality, analyticsProperty.city].filter(Boolean).join(", ")}
                    {" · "}
                    {analyticsInfo.priceLabel}{analyticsInfo.priceUnit ? ` ${analyticsInfo.priceUnit}` : ""}
                  </p>
                </div>
              </div>

              <div className="stat-strip dl-an-stats">
                <div className="stat">
                  <span className="stat-label">Total views</span>
                  <span className="stat-value">{analytics.views}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Leads generated</span>
                  <span className="stat-value">{analytics.leads}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Conversion rate</span>
                  <span className="stat-value">{analytics.conversionRate}</span>
                </div>
              </div>

              <section className="dl-an-section">
                <h4 className="dl-an-title">Leads by contact channel</h4>
                <div className="dl-an-bars">
                  {[
                    { id: "call", label: "Phone calls", count: analytics.channels.call },
                    { id: "chat", label: "Chats", count: analytics.channels.chat },
                    { id: "visit", label: "Visits / emails", count: analytics.channels.visit },
                  ].map((c) => (
                    <div key={c.id} className="dl-an-bar">
                      <div className="dl-an-bar-label">
                        <span>{c.label}</span>
                        <span className="dl-an-bar-count">{c.count}</span>
                      </div>
                      <div className="dl-an-bar-track" aria-hidden="true">
                        <div className="dl-an-bar-fill" style={{ width: `${channelShare(c.count)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="dl-an-section">
                <h4 className="dl-an-title">Traffic by device</h4>
                <div className="stat-strip dl-an-stats">
                  <div className="stat">
                    <span className="stat-label">Mobile users</span>
                    <span className="stat-value">{analytics.devices.mobile}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Desktop / web</span>
                    <span className="stat-value">{analytics.devices.desktop}</span>
                  </div>
                </div>
              </section>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setAnalyticsProperty(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
